"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppYmlGenerator = exports.BaseAppYmlGenerator = void 0;
const tslib_1 = require("tslib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const MigrationUtils_1 = require("./MigrationUtils");
const path = tslib_1.__importStar(require("path"));
const fs = tslib_1.__importStar(require("fs-extra"));
const handlebars = tslib_1.__importStar(require("handlebars"));
const folder_1 = require("../../../folder");
const versionMetadata_1 = require("../../../common/versionMetadata");
const projectSettingsHelperV3_1 = require("../../../common/projectSettingsHelperV3");
const migrate_1 = require("../../../component/migrate");
class BaseAppYmlGenerator {
    constructor(oldProjectSettings) {
        this.oldProjectSettings = oldProjectSettings;
    }
    async buildHandlebarsTemplate(templateName) {
        const templatePath = path.join(folder_1.getTemplatesFolder(), "core/v3Migration", templateName);
        const templateString = await fs.readFile(templatePath, "utf8");
        const template = handlebars.compile(templateString);
        return template(this.handlebarsContext);
    }
}
exports.BaseAppYmlGenerator = BaseAppYmlGenerator;
class AppYmlGenerator extends BaseAppYmlGenerator {
    constructor(oldProjectSettings, bicepContent, projectPath) {
        super(oldProjectSettings);
        this.bicepContent = bicepContent;
        this.projectPath = projectPath;
        this.handlebarsContext = {
            activePlugins: {},
            placeholderMappings: {},
            aadAppName: undefined,
            teamsAppName: undefined,
            appName: undefined,
            isFunctionBot: false,
            botResourceId: undefined,
            isTypescript: false,
            defaultFunctionName: undefined,
            environmentFolder: undefined,
            projectId: undefined,
            dotnetPath: "DOTNET_PATH",
            isM365: undefined,
        };
    }
    async generateAppYml() {
        var _a;
        await this.generateCommonHandlerbarsContext();
        const solutionSettings = this.oldProjectSettings.solutionSettings;
        if (solutionSettings.hostType.toLowerCase() === "azure") {
            await this.generateAzureHandlebarsContext();
            switch ((_a = this.oldProjectSettings.programmingLanguage) === null || _a === void 0 ? void 0 : _a.toLowerCase()) {
                case "javascript":
                case "typescript":
                    return await this.buildHandlebarsTemplate("js.ts.app.yml");
                case "csharp":
                    return await this.buildHandlebarsTemplate("csharp.app.yml");
            }
        }
        else if (solutionSettings.hostType.toLowerCase() === "spfx") {
            return await this.buildHandlebarsTemplate("spfx.app.yml");
        }
        throw new Error("The current tooling cannot upgrade your project temporary. Please raise an issue in GitHub for your project.");
    }
    async generateAppLocalYml(placeholderMappings) {
        var _a;
        this.handlebarsContext.placeholderMappings = placeholderMappings;
        await this.generateAzureHandlebarsContext();
        const solutionSettings = this.oldProjectSettings.solutionSettings;
        if (solutionSettings.hostType === "Azure") {
            switch ((_a = this.oldProjectSettings.programmingLanguage) === null || _a === void 0 ? void 0 : _a.toLowerCase()) {
                case "csharp":
                    return await this.buildHandlebarsTemplate("csharp.app.local.yml");
            }
        }
        throw new Error("The current tooling cannot upgrade your project temporary. Please raise an issue in GitHub for your project.");
    }
    async generateCommonHandlerbarsContext() {
        var _a;
        // project setting information
        this.handlebarsContext.appName = this.oldProjectSettings.appName;
        const azureSolutionSettings = this.oldProjectSettings.solutionSettings;
        for (const activePlugin of azureSolutionSettings.activeResourcePlugins) {
            this.handlebarsContext.activePlugins[activePlugin] = true; // convert array items to object properties to simplify handlebars template
        }
        // app names
        const aadManifestPath = path.join(this.projectPath, versionMetadata_1.MetadataV3.aadManifestFileName);
        if (await fs.pathExists(aadManifestPath)) {
            const aadManifest = await fs.readJson(path.join(this.projectPath, versionMetadata_1.MetadataV3.aadManifestFileName));
            this.handlebarsContext.aadAppName = aadManifest.name;
        }
        const teamsAppManifestPath = path.join(this.projectPath, teamsfx_api_1.AppPackageFolderName, versionMetadata_1.MetadataV3.teamsManifestFileName);
        if (await fs.pathExists(teamsAppManifestPath)) {
            const teamsAppManifest = await fs.readJson(path.join(this.projectPath, teamsfx_api_1.AppPackageFolderName, versionMetadata_1.MetadataV3.teamsManifestFileName));
            this.handlebarsContext.teamsAppName = teamsAppManifest.name.short;
        }
        // programming language
        this.handlebarsContext.isTypescript =
            ((_a = this.oldProjectSettings.programmingLanguage) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === "typescript";
        // default function name
        this.handlebarsContext.defaultFunctionName = this.oldProjectSettings.defaultFunctionName;
        // projectId
        this.handlebarsContext.projectId = this.oldProjectSettings.projectId;
        // env folder
        this.handlebarsContext.environmentFolder = versionMetadata_1.MetadataV3.defaultEnvironmentFolder;
        this.handlebarsContext.isM365 = this.oldProjectSettings.isM365;
    }
    generateAzureHandlebarsContext() {
        // isFunctionBot
        const projectSettings = migrate_1.convertProjectSettingsV2ToV3(this.oldProjectSettings, this.projectPath);
        // placeholders
        this.setPlaceholderMapping("state.fx-resource-frontend-hosting.storageResourceId");
        this.setPlaceholderMapping("state.fx-resource-frontend-hosting.endpoint");
        this.setPlaceholderMapping("state.fx-resource-frontend-hosting.resourceId");
        this.setPlaceholderMapping("state.fx-resource-frontend-hosting.indexPath");
        this.setPlaceholderMapping("state.fx-resource-bot.resourceId");
        this.setPlaceholderMapping("state.fx-resource-bot.functionAppResourceId");
        this.setPlaceholderMapping("state.fx-resource-bot.webAppResourceId");
        this.setPlaceholderMapping("state.fx-resource-bot.botWebAppResourceId");
        this.setPlaceholderMapping("state.fx-resource-function.functionAppResourceId");
        this.setPlaceholderMapping("state.fx-resource-function.functionEndpoint");
        this.handlebarsContext.isFunctionBot = projectSettingsHelperV3_1.hasFunctionBot(projectSettings); // if not function bot but a resource bot, then a webApp bot
        // Match teams-bot or fx-resource-bot output obj
        const pluginRegex = new RegExp("output +(\\S+) +object += +{" + // Mataches start of output declaration and capture output name. Example: output functionOutput object = {
            "[^{]*" + // Matches everything between '{' and plugin id declaration. For example: comments, extra properties. Will match multilines.
            "teamsFxPluginId: +'(teams-bot|fx-resource-bot)'" + // Matches given plugin id == teams-bot or fx-resource-bot
            "[^}]*" + // Mathches anything except '}'
            "(botWebAppResourceId|webAppResourceId|functionAppResourceId|resourceId) *:" + // Matches resource id and tries not to mismatch key and value
            "[^}]*}", // Matches until end of obj as '}'
        "g");
        const outputContents = pluginRegex.exec(this.bicepContent);
        if (outputContents) {
            const prefix = "state.fx-resource-bot.";
            this.handlebarsContext.botResourceId =
                this.handlebarsContext.placeholderMappings[`${prefix}${outputContents[3]}`];
        }
        return Promise.resolve();
    }
    setPlaceholderMapping(placeholder) {
        const result = MigrationUtils_1.namingConverterV3(placeholder, MigrationUtils_1.FileType.STATE, this.bicepContent);
        if (result.isOk()) {
            this.handlebarsContext.placeholderMappings[placeholder] = result.value;
        }
        // ignore non-exist placeholder
    }
}
exports.AppYmlGenerator = AppYmlGenerator;
//# sourceMappingURL=appYmlGenerator.js.map